import { FormProvider, useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from '@/components/core/form/Select';
import { useEffect } from 'react';
import { getNodeOptions } from '@/utils/pathwayUtils';
import debounce from 'debounce';
import { useNodes } from '@/stores/analysisStore';
import { useNodeParamsOptions, useParamCategories, useWorkbench, workbenchActions } from '@/stores/workbenchStore';
import { unitToString } from '@/utils/unitUtils';
import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { NumericFormatInput } from '@/components/core/form/NumericFormatInput';
import InputBase from '@/components/core/form/InputBase';
import { ANCHOR_NODE_ID, ANCHOR_PARAM_NAME } from '@/stores/workbenchStore';

const WorkbenchTableForm = ({ defaultValues, onSubmit, showAddButton, showRemoveButton }) => {
  const methods = useForm({ defaultValues });
  const { handleSubmit, watch, register, control, setValue } = methods;
  const debouncedSave = debounce(() => handleSubmit(onSubmit)(), 10);
  const nodes = useNodes();
  const nodeOptions = [
    { label: '', value: '' },
    { label: 'Pathway Basis', value: ANCHOR_NODE_ID },
    ...getNodeOptions(nodes),
  ];
  const defaultSteps = defaultValues?.steps ?? 5;
  const [nodeId, category, paramName] = useWatch({
    control,
    name: ['nodeId', 'category', 'paramName'],
    defaultValue: defaultValues,
  });

  const isAnchor = nodeId === ANCHOR_NODE_ID;
  const categories = [{ value: '', label: '' }, ...useParamCategories(nodeId)];
  const paramOptions = useNodeParamsOptions(nodeId, category);
  const param = useWorkbench(defaultValues.key);
  const defaultMin = param?.min;
  const defaultMax = param?.max;
  const baseline = param?.baseline;
  const unit = unitToString(param?.unit);

  useEffect(() => {
    if (isAnchor) {
      setValue('category', '');
      setValue('paramName', ANCHOR_PARAM_NAME);
    } else if (nodeId !== defaultValues.nodeId) {
      setValue('category', '');
      setValue('paramName', '');
    }
  }, [nodeId, isAnchor, defaultValues.nodeId, setValue]);

  useEffect(() => {
    if (!isAnchor && category !== defaultValues.category) {
      setValue('paramName', '');
    }
  }, [category, isAnchor, defaultValues.category, setValue]);

  useEffect(() => {
    setValue('min', defaultMin);
    setValue('max', defaultMax);
    setValue('unit', unit);
    setValue('baseline', baseline);
    setValue('steps', defaultSteps);
  }, [paramName, setValue, defaultMin, defaultMax, baseline, unit, defaultSteps]);

  useEffect(() => {
    const subscription = watch(debouncedSave);
    return () => subscription.unsubscribe();
  }, [watch, debouncedSave]);

  return (
    <FormProvider {...methods}>
      <tr>
        <td className="align-middle">
          <Select name="nodeId" className="select-xs min-w-full" options={nodeOptions} />
        </td>
        <td className="align-middle">
          <Select name="category" className="select-xs min-w-full" options={categories} disabled={isAnchor} />
        </td>
        <td className="align-middle">
          <Select name="paramName" className="select-xs min-w-full" options={paramOptions} disabled={isAnchor} />
        </td>
        <td className="align-middle">
          <NumericFormatInput id="min" name="min" control={control} className="!w-16 !input-xs" />
        </td>
        <td className="align-middle">
          <NumericFormatInput id="baseline" name="baseline" control={control} readOnly className="!w-16 !input-xs" />
        </td>
        <td className="align-middle">
          <NumericFormatInput id="max" name="max" control={control} className="!w-16 !input-xs" />
        </td>
        <td className="align-middle">
          <InputBase readOnly {...register('unit')} id="unit" className="!w-14 !input-xs" />
        </td>
        <td className="align-middle">
          <div className="join items-center">
            <NumericFormatInput control={control} id="steps" name="steps" className="!w-14 !input-xs" />
            {showAddButton && (
              <button onClick={workbenchActions.add} className="btn btn-xs join-item btn-ghost">
                <PlusCircleIcon className="h-5 w-5" />
              </button>
            )}

            {showRemoveButton && (
              <button
                onClick={() => workbenchActions.remove(defaultValues.key)}
                className="btn btn-xs join-item btn-ghost"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </td>
      </tr>
    </FormProvider>
  );
};

WorkbenchTableForm.propTypes = {
  defaultValues: PropTypes.object,
  pathwaysOptions: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func,
  showAddButton: PropTypes.bool,
  showRemoveButton: PropTypes.bool,
};

export default WorkbenchTableForm;
